/**
 * Cookie/tracking consent state. Stores the user's choice in localStorage
 * and mirrors it server-side via the `log-consent` edge function so we have
 * an auditable record (GDPR Art. 7(1)).
 */

import { tryInvoke } from "@/lib/invoke";

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  // Granular per-vendor flags (v2). Optional so legacy readers keep working.
  ga?: boolean;
  googleAds?: boolean;
  metaPixel?: boolean;
};

const STORAGE_KEY = "os_consent_v2";
const LEGACY_KEY = "os_consent_v1";
const ANON_KEY = "os_anon_id";

export function getAnonId(): string {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

export function getConsent(): ConsentCategories | null {
  if (typeof window === "undefined") return null;
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) {
        // Migrate v1 → v2: analytics maps to ga; marketing maps to both ad networks.
        try {
          const legacyParsed = JSON.parse(legacy);
          const migrated = {
            necessary: true,
            analytics: !!legacyParsed.analytics,
            marketing: !!legacyParsed.marketing,
            ga: !!legacyParsed.analytics,
            googleAds: !!legacyParsed.marketing,
            metaPixel: !!legacyParsed.marketing,
            ts: Date.now(),
            v: "v2",
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
          raw = JSON.stringify(migrated);
        } catch {
          /* ignore */
        }
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const analytics = !!parsed.analytics || !!parsed.ga;
    const marketing = !!parsed.marketing || !!parsed.googleAds || !!parsed.metaPixel;
    return {
      necessary: true,
      analytics,
      marketing,
      ga: parsed.ga ?? parsed.analytics ?? false,
      googleAds: parsed.googleAds ?? parsed.marketing ?? false,
      metaPixel: parsed.metaPixel ?? parsed.marketing ?? false,
    };
  } catch {
    return null;
  }
}

export type VendorKey = "analytics" | "marketing" | "ga" | "googleAds" | "metaPixel";

export function hasConsent(category: VendorKey): boolean {
  const c = getConsent();
  if (!c) return false;
  if (category === "analytics") return !!c.analytics;
  if (category === "marketing") return !!c.marketing;
  return !!c[category];
}

export function setConsent(
  categories: {
    analytics?: boolean;
    marketing?: boolean;
    ga?: boolean;
    googleAds?: boolean;
    metaPixel?: boolean;
  },
  source: string
) {
  const ga = categories.ga ?? categories.analytics ?? false;
  const googleAds = categories.googleAds ?? categories.marketing ?? false;
  const metaPixel = categories.metaPixel ?? categories.marketing ?? false;
  const analytics = ga;
  const marketing = googleAds || metaPixel;
  const value = {
    necessary: true,
    analytics,
    marketing,
    ga,
    googleAds,
    metaPixel,
    ts: Date.now(),
    v: "v2",
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // ignore quota errors
  }
  window.dispatchEvent(new CustomEvent("os:consent-changed", { detail: value }));
  // Fire-and-forget audit log
  logConsentServer(value, source).catch(() => {
    /* non-blocking */
  });
}

async function logConsentServer(
  value: { analytics: boolean; marketing: boolean; ga?: boolean; googleAds?: boolean; metaPixel?: boolean },
  source: string
) {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    await tryInvoke("log-consent", {
      requireAuth: false,
      body: {
        anon_id: getAnonId(),
        analytics: value.analytics,
        marketing: value.marketing,
        ga: value.ga ?? value.analytics,
        google_ads: value.googleAds ?? value.marketing,
        meta_pixel: value.metaPixel ?? value.marketing,
        source,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
    });
  } catch {
    /* non-blocking */
  }
}

export function clearConsent() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_KEY);
  window.dispatchEvent(new CustomEvent("os:consent-changed", { detail: null }));
}

// Accepts unknown so it can be passed straight to onClick (the click event is ignored).
export function openCookieSettings(opts?: unknown) {
  if (typeof window === "undefined") return;
  const marketingOff = !!opts && typeof opts === "object" && (opts as { marketingOff?: unknown }).marketingOff === true;
  window.dispatchEvent(new CustomEvent("os:open-cookie-settings", { detail: { marketingOff } }));
}